import { useCallback, useEffect, useRef, useState } from "react";
import {
  getConversation,
  patchStubSessionStatus,
  postConversationMessage,
  postTicketOpenedTimelineEvent,
  transferConversation,
} from "@/api/conversationsApi";
import {
  deleteLastAgentMessage,
  postResolveSession,
  postReopenSession,
} from "@/api/humanAgentOpsApi";
import { isHumanAgentInboxLive } from "@/api/inboxAgentApi";
import { surfaceInboxHttpError } from "@/lib/inboxErrorToast";
import { mergeThreadMessages, upsertThreadMessage } from "@/lib/mergeThreadMessages";
import { isSameThreadSession } from "@/lib/sameThreadSession";
import { ConversationThreadRequestCoordinator } from "../lib/ConversationThreadRequestCoordinator";

function abortError() {
  const error = new Error("Request aborted");
  error.name = "AbortError";
  return error;
}

function abortableDelay(ms, signal) {
  if (signal?.aborted) return Promise.reject(abortError());
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortError());
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

function isAbortError(error) {
  return Boolean(error && typeof error === "object" && error.name === "AbortError");
}

// Backoff for silently retrying a transient false-401 ("token not verified")
// during thread load, so the user never sees a broken error screen for what a
// page refresh would have fixed. The refresh coordinator owns the real session;
// these retries just wait out an introspection blip.
const AUTH_RETRY_DELAYS_MS = [500, 1200, 2500];

function isAuthBlip(error) {
  const status =
    error && typeof error === "object" && "status" in error && typeof error.status === "number"
      ? error.status
      : undefined;
  return status === 401 || status === 403;
}

function isNotFound(error) {
  const status =
    error && typeof error === "object" && "status" in error && typeof error.status === "number"
      ? error.status
      : undefined;
  return status === 404;
}

/**
 * Active thread: load conversation, send messages/notes, resolve/reopen, transfer.
 */
export function useConversationThread(
  conversationId,
  ticketCreatedEvent,
  {
    workspaceId,
    workspaceHint = null,
    centralInbox = false,
    onAfterMessageSent,
    threadCache = null,
  } = {},
) {
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const appliedTicketIdsRef = useRef(new Set());
  const sessionWorkspaceRef = useRef(null);
  const displayedSessionRef = useRef(null);
  // Monotonic load token: a newer load (or unmount / conversation switch)
  // supersedes any in-flight load + its pending silent retries.
  const loadSeqRef = useRef(0);
  const requestCoordinatorRef = useRef(null);
  if (!requestCoordinatorRef.current) {
    requestCoordinatorRef.current = new ConversationThreadRequestCoordinator();
  }
  // The thread currently on screen. Every async write below re-checks this
  // after its await: a response for a conversation the agent already left must
  // never land in the open thread.
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;

  const isCurrentSession = useCallback(
    (sessionId) => isSameThreadSession(sessionId, conversationIdRef.current),
    [],
  );

  const getCacheKey = useCallback(
    (sessionId = conversationId, hint = null) => {
      const keyParts = {
        sessionId,
        workspaceHint: hint ?? workspaceHint ?? workspaceId ?? null,
        centralInbox,
      };
      return (
        threadCache?.makeKey(keyParts) ??
        `${centralInbox ? "central" : "workspace"}:${String(keyParts.workspaceHint ?? "unknown")}:${String(sessionId ?? "")}`
      );
    },
    [threadCache, conversationId, workspaceHint, workspaceId, centralInbox],
  );

  const cacheThreadData = useCallback(
    (data, requestedKey = null) => {
      if (!threadCache || !data) return;
      if (requestedKey) threadCache.set(requestedKey, data);
      const canonicalKey = getCacheKey(data.id, data.workspaceId ?? null);
      if (canonicalKey && canonicalKey !== requestedKey) threadCache.set(canonicalKey, data);
    },
    [threadCache, getCacheKey],
  );

  const applyThreadData = useCallback(
    (data, { writeCache = true, requestedKey = null } = {}) => {
      if (!data) return;
      if (!isCurrentSession(data.id)) return;
      const isAlreadyDisplayed = isSameThreadSession(data.id, displayedSessionRef.current);
      displayedSessionRef.current = data.id;
      setConversation(data);
      sessionWorkspaceRef.current =
        data?.workspaceId != null && String(data.workspaceId).trim()
          ? String(data.workspaceId).trim()
          : sessionWorkspaceRef.current;
      setMessages((prev) =>
        isAlreadyDisplayed
          ? mergeThreadMessages(prev, Array.isArray(data.messages) ? data.messages : [])
          : Array.isArray(data.messages)
            ? data.messages
            : [],
      );
      if (writeCache) cacheThreadData(data, requestedKey);
      setError(null);
    },
    [isCurrentSession, cacheThreadData],
  );

  const load = useCallback(
    async ({ silent = false, force = false } = {}) => {
      if (!conversationId) {
        loadSeqRef.current += 1;
        requestCoordinatorRef.current.abort();
        displayedSessionRef.current = null;
        setConversation(null);
        setMessages([]);
        setLoading(false);
        setError(null);
        return;
      }
      const seq = (loadSeqRef.current += 1);
      const cacheKey = getCacheKey(conversationId);
      const cached = !force && cacheKey ? threadCache?.read(cacheKey) : null;

      if (cached?.status === "fresh" || cached?.status === "stale") {
        applyThreadData(cached.data, { writeCache: false });
        setLoading(false);
        if (cached.status === "fresh") return;
        silent = true;
      } else if (!silent) {
        displayedSessionRef.current = null;
        setConversation(null);
        setMessages([]);
        setLoading(true);
        setError(null);
        appliedTicketIdsRef.current = new Set();
        sessionWorkspaceRef.current = null;
      }

      try {
        const data = await requestCoordinatorRef.current.request(cacheKey, async (signal) => {
          let attempt = 0;
          for (;;) {
            try {
              const result = await getConversation({
                id: conversationId,
                workspaceId,
                centralInbox,
                sessionWorkspaceId:
                  sessionWorkspaceRef.current ?? workspaceHint ?? null,
                signal,
              });
              return result.data;
            } catch (e) {
              if (isAbortError(e)) throw e;
              if (isAuthBlip(e) && attempt < AUTH_RETRY_DELAYS_MS.length) {
                surfaceInboxHttpError(e);
                await abortableDelay(AUTH_RETRY_DELAYS_MS[attempt], signal);
                attempt += 1;
                continue;
              }
              throw e;
            }
          }
        });
        if (seq !== loadSeqRef.current) return;
        applyThreadData(data, { requestedKey: cacheKey });
        if (!silent) setLoading(false);
      } catch (e) {
        if (seq !== loadSeqRef.current || isAbortError(e)) return;

        if (isNotFound(e)) {
          setConversation(null);
          setMessages([]);
          displayedSessionRef.current = null;
          setError(null);
          threadCache?.invalidateSession(conversationId);
          if (!silent) setLoading(false);
          return;
        }

        surfaceInboxHttpError(e);
        if (!silent && !isAuthBlip(e)) {
          setError(e instanceof Error ? e : new Error(String(e)));
          setConversation(null);
          setMessages([]);
          setLoading(false);
        } else if (!silent) {
          setLoading(false);
        }
      }
    },
    [
      conversationId,
      workspaceId,
      workspaceHint,
      centralInbox,
      getCacheKey,
      threadCache,
      applyThreadData,
    ],
  );

  useEffect(() => {
    void load();
    // Supersede any in-flight load + pending silent retries on unmount or when
    // the conversation/workspace changes (load's identity tracks those deps).
    return () => {
      loadSeqRef.current += 1;
      requestCoordinatorRef.current.abort();
    };
  }, [load]);

  const sessionWorkspaceId =
    conversation?.workspaceId != null && String(conversation.workspaceId).trim()
      ? String(conversation.workspaceId).trim()
      : null;

  const mutationWorkspaceId = centralInbox ? sessionWorkspaceId : workspaceId ?? null;

  const updateCachedSession = useCallback(
    (updater) => {
      if (!conversationId) return;
      threadCache?.updateSession(conversationId, updater);
    },
    [conversationId, threadCache],
  );

  const invalidateCache = useCallback(() => {
    if (!conversationId) return;
    threadCache?.invalidateSession(conversationId, sessionWorkspaceRef.current);
  }, [conversationId, threadCache]);

  useEffect(() => {
    if (!ticketCreatedEvent || ticketCreatedEvent.conversationId !== conversationId) return;
    const tid = ticketCreatedEvent.ticketId;
    if (!tid || appliedTicketIdsRef.current.has(tid)) return;
    appliedTicketIdsRef.current.add(tid);
    let cancelled = false;
    (async () => {
      try {
        const { data: msg } = await postTicketOpenedTimelineEvent({
          conversationId,
          ticketId: tid,
        });
        if (cancelled || !msg) return;
        setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
      } catch {
        appliedTicketIdsRef.current.delete(tid);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ticketCreatedEvent, conversationId]);

  const sendMessage = useCallback(
    async (text, activeTab, { attachmentIds, attachments } = {}) => {
      const trimmed = (text || "").trim();
      const ids = Array.isArray(attachmentIds) ? attachmentIds.filter(Boolean) : [];
      if ((!trimmed && !ids.length) || !conversationId) return;
      const sender = activeTab === "note" ? "note" : "agent";
      try {
        const { data: msg } = await postConversationMessage({
          conversationId,
          text: trimmed,
          sender,
          workspaceId: mutationWorkspaceId,
          attachmentIds: ids,
          attachments,
        });
        if (msg && isCurrentSession(conversationId)) {
          const appendSentMessage = (currentMessages) =>
            upsertThreadMessage(
              currentMessages.map((item) =>
                item.canDelete ? { ...item, canDelete: false } : item,
              ),
              msg,
            );
          setMessages(appendSentMessage);
          updateCachedSession((cached) => ({
            ...cached,
            messages: appendSentMessage(Array.isArray(cached.messages) ? cached.messages : []),
          }));
          onAfterMessageSent?.(msg);
        }
      } catch (e) {
        surfaceInboxHttpError(e);
        throw e;
      }
    },
    [
      conversationId,
      mutationWorkspaceId,
      onAfterMessageSent,
      isCurrentSession,
      updateCachedSession,
    ]
  );

  const appendMessage = useCallback(
    (msg) => {
      if (!msg) return;
      if (!isCurrentSession(msg.sessionId)) return;
      const appendRealtimeMessage = (currentMessages) => {
        const alreadyPresent = currentMessages.some(
          (item) => String(item.id) === String(msg.id),
        );
        const base = alreadyPresent
          ? currentMessages
          : currentMessages.map((item) =>
              item.canDelete ? { ...item, canDelete: false } : item,
            );
        return upsertThreadMessage(base, msg);
      };
      setMessages(appendRealtimeMessage);
      updateCachedSession((cached) => ({
        ...cached,
        messages: appendRealtimeMessage(Array.isArray(cached.messages) ? cached.messages : []),
      }));
    },
    [isCurrentSession, updateCachedSession],
  );

  const removeMessage = useCallback(
    (messageId) => {
      if (messageId == null || String(messageId).trim() === "") return;
      const canonicalId = String(messageId ?? "").startsWith("hm-")
        ? String(messageId)
        : `hm-${messageId}`;
      const withoutDeleted = (currentMessages) =>
        currentMessages.filter((item) => String(item.id) !== canonicalId);
      setMessages(withoutDeleted);
      updateCachedSession((cached) => ({
        ...cached,
        messages: withoutDeleted(Array.isArray(cached.messages) ? cached.messages : []),
      }));
    },
    [updateCachedSession],
  );

  const deleteMessage = useCallback(
    async (message) => {
      if (!conversationId || !message?.canDelete || !mutationWorkspaceId) return;
      try {
        const result = await deleteLastAgentMessage({
          sessionId: conversationId,
          messageId: message.id,
          workspaceId: mutationWorkspaceId,
        });
        if (!isCurrentSession(conversationId)) return;
        const applyDeletion = (currentMessages) =>
          result?.visible_to_requester
            ? currentMessages.map((item) =>
                item.id === message.id
                  ? {
                      ...item,
                      canDelete: false,
                      deleted: true,
                      deletedAt: new Date(result.deleted_at).toISOString(),
                      deletedByAgentId: String(result.deleted_by_agent_id),
                    }
                  : item,
              )
            : currentMessages.filter((item) => item.id !== message.id);
        setMessages(applyDeletion);
        updateCachedSession((cached) => ({
          ...cached,
          messages: applyDeletion(Array.isArray(cached.messages) ? cached.messages : []),
        }));
        await load({ silent: true, force: true });
      } catch (e) {
        surfaceInboxHttpError(e);
        throw e;
      }
    },
    [
      conversationId,
      mutationWorkspaceId,
      isCurrentSession,
      updateCachedSession,
      load,
    ],
  );

  const resolveSession = useCallback(async () => {
    if (!conversationId) return;
    if (isHumanAgentInboxLive()) {
      try {
        await postResolveSession({ sessionId: conversationId, workspaceId: mutationWorkspaceId });
      } catch (e) {
        surfaceInboxHttpError(e);
        throw e;
      }
    } else {
      patchStubSessionStatus({ conversationId, status: "resolved" });
    }
    updateCachedSession((cached) => ({ ...cached, status: "resolved" }));
    await load({ silent: true, force: true });
  }, [conversationId, mutationWorkspaceId, load, updateCachedSession]);

  const reopenSession = useCallback(async () => {
    if (!conversationId) return;
    if (isHumanAgentInboxLive()) {
      try {
        await postReopenSession({ sessionId: conversationId, workspaceId: mutationWorkspaceId });
      } catch (e) {
        surfaceInboxHttpError(e);
        throw e;
      }
    } else {
      patchStubSessionStatus({ conversationId, status: "open" });
    }
    updateCachedSession((cached) => ({ ...cached, status: "active" }));
    await load({ silent: true, force: true });
  }, [conversationId, mutationWorkspaceId, load, updateCachedSession]);

  const transferChat = useCallback(
    /**
     * In live mode: toAgentId (int) is required.
     * In stub mode: toAssignee (string) is used.
     * @param {string | number} toAgentOrAssignee
     */
    async (toAgentOrAssignee) => {
      if (!conversationId) return;
      const isLive = isHumanAgentInboxLive();
      try {
        const { data } = await transferConversation({
          conversationId,
          toAgentId: isLive ? Number(toAgentOrAssignee) : undefined,
          toAssignee: isLive ? undefined : String(toAgentOrAssignee),
          workspaceId: mutationWorkspaceId,
          centralInbox,
          sessionWorkspaceId,
        });
        if (!isCurrentSession(conversationId)) return;
        if (data) {
          applyThreadData(data);
        } else {
          await load({ force: true });
        }
      } catch (e) {
        surfaceInboxHttpError(e);
        throw e;
      }
    },
    [
      conversationId,
      mutationWorkspaceId,
      centralInbox,
      sessionWorkspaceId,
      load,
      isCurrentSession,
      applyThreadData,
    ]
  );

  return {
    conversation,
    messages,
    loading,
    error,
    reload: load,
    invalidateCache,
    applyThreadData,
    sendMessage,
    deleteMessage,
    appendMessage,
    removeMessage,
    resolveSession,
    reopenSession,
    transferChat,
  };
}
